/**
 * Subdomain Detection Utility
 * 
 * Handles detection of organization subdomains from the hostname.
 * 
 * Production setup:
 * - Main app: app.sporthouse.ai
 * - Org portals: springfield.sporthouse.ai, valley.sporthouse.ai, etc.
 * 
 * Development:
 * - localhost:3000 = main app
 * - Path-based fallback: /org/:subdomain still works
 */

// Base domain for production (without the 'app.' prefix)
const BASE_DOMAIN = 'sporthouse.ai';
const WEBSITE_PRO_DOMAIN = 'sporthouse.site';
const WEBSITE_PRO_PILOT_HOSTS = new Set(['sjfc.sporthouse.ai']);

// Reserved subdomains that should be treated as the main app
const RESERVED_SUBDOMAINS = [
  'app',      // Main application
  'www',      // Redirect or main
  'api',      // API server
  'admin',    // Admin panel (if separate)
  'mail',     // Email
  'smtp',     // Email
  'ftp',      // FTP
  'dev',      // Development
  'staging',  // Staging
  'test',     // Testing
  'preview',  // Preview environments
];

const isLocalOrPreviewHost = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.pages.dev') ||
    hostname.includes('.preview.') ||
    hostname.includes('.vercel.app') ||
    hostname.includes('.netlify.app') ||
    hostname.includes('.emergent');
};

/**
 * Extract subdomain from the current hostname
 * 
 * @returns {string|null} The organization subdomain, or null if on main app
 * 
 * Examples:
 * - springfield.sporthouse.ai → 'springfield'
 * - app.sporthouse.ai → null (main app)
 * - localhost:3000 → null (development)
 * - youthsports-1.preview.emergentagent.com → null (preview environment)
 */
export function getSubdomain() {
  const hostname = window.location.hostname;
  
  // Development: localhost is always main app
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  
  // Preview/staging environments (Emergent, Vercel, Netlify, etc.)
  if (
    hostname.includes('.preview.') ||
    hostname.includes('.vercel.app') ||
    hostname.includes('.netlify.app') ||
    hostname.includes('.herokuapp.com') ||
    hostname.includes('.emergent')
  ) {
    return null;
  }
  
  // Check if it's a subdomain of the base domain
  if (hostname.endsWith(`.${BASE_DOMAIN}`)) {
    // Extract the subdomain part
    const subdomain = hostname.replace(`.${BASE_DOMAIN}`, '');
    
    // Check if it's a reserved subdomain
    if (RESERVED_SUBDOMAINS.includes(subdomain.toLowerCase())) {
      return null;
    }
    
    // It's an org subdomain
    return subdomain.toLowerCase();
  }
  
  // Not on our domain at all - treat as main app
  return null;
}

/**
 * Check if we're on an organization subdomain
 */
export function isOrgSubdomain() {
  return getSubdomain() !== null;
}

export function isWebsiteProHostname(hostname) {
  hostname = hostname.toLowerCase();
  return WEBSITE_PRO_PILOT_HOSTS.has(hostname) ||
    (hostname.endsWith(`.${WEBSITE_PRO_DOMAIN}`) && hostname !== `www.${WEBSITE_PRO_DOMAIN}`);
}

// Only recognized Website Pro hosts carry an organization slug.
export function websiteProOrganizationSlug(hostname = window.location.hostname) {
  const host = hostname.toLowerCase();
  if (!isWebsiteProHostname(host)) return null;
  const slug = host.split('.')[0];
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && host.split('.').length === 3 ? slug : null;
}

export function isWebsiteProHost() {
  return isWebsiteProHostname(window.location.hostname);
}

export function isWebsiteProAdminPath(pathname) {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export function isAdminPortalHost() {
  return window.location.hostname === `admin.${BASE_DOMAIN}`;
}

// The canonical staging origin serves both organization and platform workflows.
// Keep the production portal flag's exclusive-host semantics unchanged.
export function isStagingSharedPortal() {
  return process.env.REACT_APP_STAGING_PUBLIC_CONFIG === 'true'
    && window.location.origin === 'https://sporthouse-admin-preview.pages.dev';
}

export function isAdminPortalEnabledForHostname(hostname, enabled) {
  const normalizedHostname = hostname.toLowerCase();
  const isAdminHostname = normalizedHostname === `admin.${BASE_DOMAIN}`;
  const isPreviewHostname = normalizedHostname === 'localhost' ||
    normalizedHostname === '127.0.0.1' ||
    normalizedHostname.endsWith('.pages.dev') ||
    normalizedHostname.includes('.preview.') ||
    normalizedHostname.includes('.vercel.app') ||
    normalizedHostname.includes('.netlify.app') ||
    normalizedHostname.includes('.emergent');

  return isAdminHostname || (enabled === 'true' && isPreviewHostname);
}

export function getAdminPortalUrl(path = "/") {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (isLocalOrPreviewHost()) {
    return `${window.location.origin}${normalizedPath}`;
  }
  return `https://admin.${BASE_DOMAIN}${normalizedPath}`;
}

export function isAdminPortalEnabled() {
  return isAdminPortalEnabledForHostname(
    window.location.hostname,
    process.env.REACT_APP_ADMIN_PORTAL_ENABLED,
  );
}

/**
 * Get the main app URL
 */
export function getMainAppUrl() {
  if (isLocalOrPreviewHost()) {
    return window.location.origin;
  }
  return `https://app.${BASE_DOMAIN}`;
}

/**
 * Get the org portal URL for a given subdomain
 */
export function getOrgPortalUrl(subdomain) {
  if (isLocalOrPreviewHost()) {
    // In development, use path-based routing
    return window.location.origin;
  }
  return `https://${subdomain}.${BASE_DOMAIN}`;
}

/**
 * Build a URL that works both in subdomain and path-based routing
 * 
 * @param {string} path - The path (e.g., '/login', '/register')
 * @param {string|null} subdomain - The subdomain (from URL param or detected)
 */
export function buildOrgUrl(path, subdomain) {
  const currentSubdomain = getSubdomain();
  
  if (currentSubdomain) {
    // We're on a true subdomain - just return the path
    return path;
  } else if (subdomain) {
    // We're using path-based routing
    return `/org/${subdomain}${path}`;
  }
  
  return path;
}
